import JoditEditor from 'jodit-react';
import {
  ComponentProps,
  ComponentRef,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';

import classNames from 'classnames';
import './JoditTextEditor.css';

export interface JoditTextEditorType {
  resetHistory: () => void;
}

type JoditConfig = NonNullable<ComponentProps<typeof JoditEditor>['config']>;

//do not change this default buttons constant value
const DEFAULT_BUTTONS: JoditConfig['buttons'] =
  'cut,copy,paste,|,undo,redo,|,eraser,|,' +
  'bold,italic,underline,strikethrough,|,' +
  'fontsize,paragraph,brush,|,indent,outdent,|,ul,ol';

interface Props {
  value: string;
  className?: string;
  classNameEditor?: string;
  buttons?: JoditConfig['buttons'];
  onTextChanged?: (value: string) => void;
  onChange?: (value: string) => void;
}

export const JoditTextEditor = forwardRef<JoditTextEditorType, Props>(
  (
    {
      value,
      className,
      classNameEditor,
      buttons = DEFAULT_BUTTONS,
      onTextChanged,
      onChange,
    },
    ref,
  ) => {
    const editorRef = useRef<ComponentRef<typeof JoditEditor>>(null);

    const config = useMemo<JoditConfig>(
      () => ({
        ...DEFAULT_CONFIG,
        editorClassName: classNames('jodit-text-editor__editor', classNameEditor),
        buttons,
        placeholder: '',
      }),
      [buttons, classNameEditor],
    );

    const handleOnTextChanged = useCallback(
      (newValue: string) => {
        if (value === newValue) return;
        onTextChanged?.(newValue);
      },
      [onTextChanged, value],
    );

    useImperativeHandle(ref, () => ({
      resetHistory: () => editorRef.current?.history?.clear(),
    }));

    return (
      <JoditEditor
        ref={editorRef}
        value={value}
        className={classNames('jodit-text-editor', className)}
        config={config}
        // by performance reasons, "onBlur" event is preferred (instead of "onChange")
        onBlur={handleOnTextChanged}
        onChange={onChange}
      />
    );
  },
);

//do not change these disabled plugins
const disablePlugins: JoditConfig['disablePlugins'] =
  'about,add-new-line,ai-assistant,backspace,class-span,' +
  'drag-and-drop,drag-and-drop-element,dtd,enter,file,' +
  'fullsize,iframe,image,image-processor,image-properties,' +
  'inline-popup,key-arrow-outside,link,media,mobile,' +
  'powered-by-jodit,preview,print,resize-cells,resize-handler,' +
  'resizer,search,select,select-cells,size,source,' +
  'speech-recognize,spellcheck,sticky,symbols,tab,table,' +
  'table-keyboard-navigation,video,wrap-nodes,xpath';

const DEFAULT_CONFIG: JoditConfig = {
  defaultActionOnPaste: 'insert_only_text',
  disablePlugins,
  askBeforePasteHTML: false,
  askBeforePasteFromWord: false,
  uploader: {
    url: 'none', // disable paste image from clipboard
  },
  inline: false,
};
